import type { TFunction } from 'i18next';
import type { FieldListItem, ShelfItem } from '@eis/patterns/reporting';
import { displayFieldName } from '@eis/patterns/reporting';
import type { DatasetField } from '@eis/core/reporting';
import { clauseText } from '../../report-viewer/model/filters';
import { valueKey, type FieldType, type ReportDocument, type Shelf } from '../../../lib/document';

/** The editor's field list (U-1): the dataset's fields, then the report's calculated fields. */
export function fieldListOf(fields: readonly DatasetField[], doc: ReportDocument): FieldListItem[] {
  return [
    ...fields.map((f) => ({ name: f.name ?? '', type: f.type ?? 'STRING', group: f.group ?? '' })),
    ...(doc.calculatedFields ?? []).map((c) => ({
      name: c.name,
      type: 'CALCULATED',
      group: '',
      ...(c.label === undefined ? {} : { label: c.label }),
    })),
  ];
}

/** The catalog type of every field the report can use, calculated fields by their declared type. */
export function typesOf(
  fields: readonly DatasetField[],
  doc: ReportDocument,
): Map<string, FieldType> {
  const types = new Map<string, FieldType>(
    fields.map((f) => [f.name ?? '', (f.type ?? 'STRING') as FieldType]),
  );
  for (const c of doc.calculatedFields ?? []) types.set(c.name, c.type);
  return types;
}

/** The tokens of one shelf, as the shelf shows them (mockup board 4). */
export function shelfItemsOf(
  doc: ReportDocument,
  shelf: Shelf,
  t: TFunction<'reporting'>,
  locale: string,
): ShelfItem[] {
  const quoted = (label: string | undefined): string | undefined =>
    label === undefined ? undefined : `„${label}“`;
  switch (shelf) {
    case 'filters':
      return (doc.filters?.clauses ?? []).map((c, i) => ({
        key: String(i),
        label: c.label ?? displayFieldName(c.field),
        detail: clauseText(c, locale),
        tag: c.editable === true ? t('editor.filterEditable') : t('editor.filterFixed'),
      }));
    case 'rows':
    case 'columns': {
      if (doc.reportType === 'TABLE' && shelf === 'columns') {
        return (doc.table?.columns ?? []).map((c) => ({
          key: c.field,
          label: displayFieldName(c.field),
          ...(c.label === undefined ? {} : { detail: quoted(c.label) }),
        }));
      }
      const list = shelf === 'rows' ? (doc.pivot?.rows ?? []) : (doc.pivot?.columns ?? []);
      return list.map((g) => ({
        key: g.field,
        label: displayFieldName(g.field),
        ...(g.label === undefined ? {} : { detail: quoted(g.label) }),
        ...(g.bucket === undefined ? {} : { tag: t(`editor.buckets.${g.bucket}`).toUpperCase() }),
      }));
    }
    case 'values':
      return (doc.pivot?.values ?? []).map((v) => {
        const format = doc.formats?.[valueKey(v)];
        const currency =
          format?.kind === 'CURRENCY' ? ' · €' : format?.kind === 'PERCENT' ? ' · %' : '';
        const detail =
          v.label === undefined
            ? currency === ''
              ? undefined
              : currency.slice(3)
            : `${quoted(v.label) ?? ''}${currency}`;
        return {
          key: valueKey(v),
          label: `${t(`editor.aggregations.${v.aggregation}`).toUpperCase()} ${displayFieldName(v.field)}`,
          ...(detail === undefined ? {} : { detail }),
        };
      });
  }
}

/** The shelf title per report type (board 5: a chart's axes). */
export function shelfTitle(doc: ReportDocument, shelf: Shelf, t: TFunction<'reporting'>): string {
  if (doc.reportType === 'TABLE' && shelf === 'columns') return t('editor.shelves.tableColumns');
  if (doc.reportType === 'CHART') {
    if (shelf === 'rows') return t('editor.shelves.chartRows');
    if (shelf === 'columns') return t('editor.shelves.chartColumns');
    if (shelf === 'values') return t('editor.shelves.chartValues');
  }
  return t(`editor.shelves.${shelf}`);
}

/** The operators a field of a type offers (the PropertyFilter table, SDD-017 §5.2; U-3). */
export function operatorsFor(type: FieldType | undefined): string[] {
  switch (type) {
    case 'NUMBER':
    case 'DATE':
    case 'DATETIME':
      // `[]` is "zwischen": two clauses, `>=` and `<=` (model/edits RANGE_OP).
      return ['[]', '==', '!=', '>', '<', '>=', '<=', '=()', '!()', '==$null', '!=$null'];
    case 'BOOLEAN':
      return ['==', '!=', '==$null', '!=$null'];
    default:
      return ['==', '!=', '=@', '!@', '=~', '!~', '=()', '!()', '==$null', '!=$null'];
  }
}
